import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import StatsOverview from './StatsOverview';
import PerformanceChart from './PerformanceChart';
import BookmakerBreakdown from './BookmakerBreakdown';
import BetTracker from './BetTracker';
import MonthlyCalendar from './MonthlyCalendar';
import SportBreakdown from './SportBreakdown';
import RoundBreakdown from './RoundBreakdown';
import Toast from '../Toast/Toast';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import { useAuth } from '../../context/AuthContext';

function ResultsTracker() {
    const { logout } = useAuth();
    
    // Page entrance animation state
    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const [isViewTransitioning, setIsViewTransitioning] = useState(false);
    const [displayedView, setDisplayedView] = useState('overview');
    const viewChangeTimeoutRef = useRef(null);

    // Loading and error states
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // Toast notification state
    const [toast, setToast] = useState(null);

    // Bet data
    const [bets, setBets] = useState([]);

    const [activeView, setActiveView] = useState('overview'); // 'overview' or 'tracker'
    const [displayMode, setDisplayMode] = useState('dollars'); // 'dollars' or 'units'
    const [unitSize, setUnitSize] = useState(25); // Default unit size
    const [advancedUnitSettings, setAdvancedUnitSettings] = useState(false);
    const [showUnitSettingsModal, setShowUnitSettingsModal] = useState(false);
    const [monthlyUnits, setMonthlyUnits] = useState([]); // Array of { startDate: 'YYYY-MM-DD', unitSize: number }
    const [sportFilter, setSportFilter] = useState('all'); // 'all' or specific sport
    
    // CSV Upload states
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [csvData, setCsvData] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [uploadError, setUploadError] = useState(null);
    const [uploadStep, setUploadStep] = useState('upload'); // 'upload', 'mapping', 'preview', 'success'
    const [previewData, setPreviewData] = useState([]);
    const [selectedSport, setSelectedSport] = useState('nba'); // Default sport for upload
    const [customUploadSport, setCustomUploadSport] = useState(''); // Custom sport name for CSV upload
    const customUploadSportRef = useRef(''); // Ref for uncontrolled input to avoid re-renders
    const [isUploading, setIsUploading] = useState(false);
    const [uploadMode, setUploadMode] = useState('replace'); // 'replace' or 'add'
    const fileInputRef = useRef(null);
    
    // Sport options: hardcoded NBA/AFL + any additional sports found in bet data
    const sportOptions = useMemo(() => {
        const hardcoded = [
            { value: 'nba', label: 'NBA' },
            { value: 'afl', label: 'AFL' }
        ];
        const hardcodedValues = new Set(hardcoded.map(s => s.value));
        const extraSports = [...new Set(bets.map(b => b.sport).filter(s => s && !hardcodedValues.has(s)))].sort();
        return [
            ...hardcoded,
            ...extraSports.map(s => ({ value: s, label: s.toUpperCase() }))
        ];
    }, [bets]);

    // Show toast notification
    const showToast = useCallback((message, type = 'error', duration = 5000) => {
        setToast({ message, type, duration });
    }, []);

    // Handle auth errors
    const handleAuthError = useCallback((errorCode) => {
        let message = 'Please login to continue.';
        if (errorCode === 'TOKEN_EXPIRED') {
            message = 'Your session has expired. Please login again.';
        }
        showToast(message, 'warning');
        setTimeout(() => logout(), 1500);
    }, [logout, showToast]);

    // Load data from API on mount
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setLoadError(null);
            
            const result = await resultsTrackerService.getAllData();
            
            if (result.success) {
                setBets(result.data.bets || []);
                if (result.data.unitSettings) {
                    setDisplayMode(result.data.unitSettings.displayMode || 'dollars');
                    setUnitSize(result.data.unitSettings.unitSize || 25);
                    setAdvancedUnitSettings(result.data.unitSettings.advancedUnitSettings || false);
                    setMonthlyUnits(result.data.unitSettings.monthlyUnits || []);
                }
                // Trigger entrance animation after data loads
                setTimeout(() => setIsPageLoaded(true), 50);
            } else {
                if (result.isAuthError) {
                    handleAuthError(result.errorCode);
                } else {
                    setLoadError(result.message);
                    showToast(result.message, 'error');
                }
            }
            
            setIsLoading(false);
        };

        loadData();
    }, [handleAuthError, showToast]);

    // Save unit settings to API (debounced)
    const settingsSaveTimeoutRef = useRef(null);
    const saveSettings = useCallback(async (settings) => {
        // Clear any pending save
        if (settingsSaveTimeoutRef.current) {
            clearTimeout(settingsSaveTimeoutRef.current);
        }
        
        // Debounce the save
        settingsSaveTimeoutRef.current = setTimeout(async () => {
            const result = await resultsTrackerService.updateSettings(settings);
            
            if (!result.success) {
                if (result.isAuthError) {
                    handleAuthError(result.errorCode);
                } else {
                    showToast('Failed to save settings: ' + result.message, 'error');
                }
            }
        }, 500);
    }, [handleAuthError, showToast]);

    // Update settings and save to API
    const updateDisplayMode = useCallback((mode) => {
        setDisplayMode(mode);
        saveSettings({
            displayMode: mode,
            unitSize,
            advancedUnitSettings,
            monthlyUnits
        });
    }, [unitSize, advancedUnitSettings, monthlyUnits, saveSettings]);

    const updateUnitSize = useCallback((size) => {
        setUnitSize(size);
        saveSettings({
            displayMode,
            unitSize: size,
            advancedUnitSettings,
            monthlyUnits
        });
    }, [displayMode, advancedUnitSettings, monthlyUnits, saveSettings]);

    const updateAdvancedSettings = useCallback((advanced) => {
        setAdvancedUnitSettings(advanced);
        saveSettings({
            displayMode,
            unitSize,
            advancedUnitSettings: advanced,
            monthlyUnits
        });
    }, [displayMode, unitSize, monthlyUnits, saveSettings]);

    const updateMonthlyUnits = useCallback((units) => {
        setMonthlyUnits(units);
        saveSettings({
            displayMode,
            unitSize,
            advancedUnitSettings,
            monthlyUnits: units
        });
    }, [displayMode, unitSize, advancedUnitSettings, saveSettings]);

    // Required fields for bet data (mappable from CSV columns)
    const requiredFields = [
        { key: 'date', label: 'Date', description: 'Bet date (DD-MM-YYYY format)' },
        { key: 'game', label: 'Game', description: 'Game/match name' },
        { key: 'bet', label: 'Bet', description: 'Bet description' },
        { key: 'odds', label: 'Odds', description: 'Decimal odds' },
        { key: 'stake', label: 'Stake', description: 'Amount staked' },
        { key: 'payout', label: 'Payout', description: 'Potential/actual payout' },
        { key: 'bookie', label: 'Bookie', description: 'Bookmaker name' },
        { key: 'result', label: 'Result', description: 'win, loss, cash out, or pending' },
        { key: 'pl', label: 'P/L', description: 'Profit/Loss amount' }
    ];
    
    // All fields for display (including sport which is selected separately)
    const allFields = [
        { key: 'date', label: 'Date', description: 'Bet date (DD-MM-YYYY format)' },
        { key: 'sport', label: 'Sport', description: 'Sport type (NBA, AFL)' },
        ...requiredFields.filter(f => f.key !== 'date')
    ];

    // Parse CSV file
    const parseCSV = (text) => {
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        if (lines.length < 2) {
            throw new Error('CSV file must contain at least a header row and one data row');
        }

        // Parse header
        const headers = parseCSVLine(lines[0]);
        
        // Parse data rows
        const data = [];
        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.length === headers.length) {
                const row = {};
                headers.forEach((header, index) => {
                    row[header] = values[index];
                });
                data.push(row);
            }
        }

        return { headers, data };
    };

    // Parse a single CSV line handling quotes
    const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current.trim());
        return result;
    };

    // Handle file upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadError(null);

        if (!file.name.endsWith('.csv')) {
            setUploadError('Please upload a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const { headers, data } = parseCSV(e.target.result);
                
                if (data.length === 0) {
                    throw new Error('No data rows found in CSV file');
                }

                setCsvHeaders(headers);
                setCsvData(data);
                
                // Auto-map columns with matching names
                const autoMapping = {};
                requiredFields.forEach(field => {
                    const matchingHeader = headers.find(h => 
                        h.toLowerCase().replace(/[^a-z0-9]/g, '') === field.key.toLowerCase() ||
                        h.toLowerCase().includes(field.key.toLowerCase())
                    );
                    if (matchingHeader) {
                        autoMapping[field.key] = matchingHeader;
                    }
                });
                setColumnMapping(autoMapping);
                setUploadStep('mapping');
            } catch (err) {
                setUploadError(err.message);
            }
        };
        reader.onerror = () => {
            setUploadError('Failed to read file');
        };
        reader.readAsText(file);
    };

    // Validate and transform data based on mapping
    const validateAndTransform = () => {
        // Sync ref value to state before validation (in case user hasn't blurred)
        if (selectedSport === '__other__' && customUploadSportRef.current) {
            setCustomUploadSport(customUploadSportRef.current);
        }
        const effectiveCustomSport = selectedSport === '__other__' ? (customUploadSportRef.current || customUploadSport) : '';
        
        const errors = [];
        
        // Check all required fields are mapped
        const unmappedFields = requiredFields.filter(f => !columnMapping[f.key]);
        if (unmappedFields.length > 0) {
            errors.push(`Missing mappings for: ${unmappedFields.map(f => f.label).join(', ')}`);
        }

        // Validate custom sport name if "Other" is selected
        if (selectedSport === '__other__' && !effectiveCustomSport.trim()) {
            errors.push('Please enter a sport name');
        }

        if (errors.length > 0) {
            setUploadError(errors.join('\n'));
            return false;
        }

        // Transform and validate data
        const transformedData = [];
        const rowErrors = [];

        csvData.forEach((row, index) => {
            const rowNum = index + 2; // +2 for header row and 0-indexing
            
            try {
                // Extract values using mapping
                const date = row[columnMapping.date]?.trim();
                const sport = selectedSport === '__other__' ? effectiveCustomSport.toLowerCase().trim() : selectedSport; // Use selected sport from dropdown
                const game = row[columnMapping.game]?.trim();
                const bet = row[columnMapping.bet]?.trim();
                const oddsStr = row[columnMapping.odds]?.trim();
                const stakeStr = row[columnMapping.stake]?.trim();
                const payoutStr = row[columnMapping.payout]?.trim();
                const bookie = row[columnMapping.bookie]?.trim();
                const result = row[columnMapping.result]?.trim().toLowerCase();
                const plStr = row[columnMapping.pl]?.trim();

                // Validate required fields
                if (!date) throw new Error(`Missing date`);
                if (!game) throw new Error(`Missing game`);
                if (!bet) throw new Error(`Missing bet`);
                if (!bookie) throw new Error(`Missing bookie`);
                if (!result) throw new Error(`Missing result`);

                // Parse and validate numeric fields
                const odds = parseFloat(oddsStr?.replace(/[^0-9.-]/g, ''));
                const stake = parseFloat(stakeStr?.replace(/[^0-9.-]/g, ''));
                const payout = parseFloat(payoutStr?.replace(/[^0-9.-]/g, ''));
                const pl = parseFloat(plStr?.replace(/[^0-9.-]/g, ''));

                if (isNaN(odds)) throw new Error(`Invalid odds: ${oddsStr}`);
                if (isNaN(stake)) throw new Error(`Invalid stake: ${stakeStr}`);
                if (isNaN(payout) && payout !== 0) throw new Error(`Invalid payout: ${payoutStr}`);
                if (isNaN(pl)) throw new Error(`Invalid P/L: ${plStr}`);

                // Validate result
                const validResults = ['win', 'loss', 'cash out', 'cashout', 'pending'];
                const normalizedResult = result === 'cashout' ? 'cash out' : result;
                if (!validResults.includes(result) && !validResults.includes(normalizedResult)) {
                    throw new Error(`Invalid result: ${result}`);
                }

                // Validate and parse date format (DD-MM-YYYY)
                const dateParts = date.split('-');
                if (dateParts.length !== 3) throw new Error(`Invalid date format: ${date}. Expected DD-MM-YYYY`);
                
                const [day, month, year] = dateParts;
                const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                
                if (isNaN(dateObj.getTime())) throw new Error(`Invalid date: ${date}`);
                
                // Format as YYYY-MM-DD for internal storage
                const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

                transformedData.push({
                    date: formattedDate,
                    sport,
                    game,
                    bet,
                    odds,
                    stake,
                    payout: isNaN(payout) ? 0 : payout,
                    bookie,
                    result: normalizedResult,
                    pl
                });
            } catch (err) {
                rowErrors.push(`Row ${rowNum}: ${err.message}`);
            }
        });

        if (rowErrors.length > 0) {
            const maxErrors = 5;
            const errorMsg = rowErrors.slice(0, maxErrors).join('\n') + 
                (rowErrors.length > maxErrors ? `\n... and ${rowErrors.length - maxErrors} more errors` : '');
            setUploadError(errorMsg);
            return false;
        }

        // Sort by date and assign IDs
        transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
        transformedData.forEach((bet, index) => {
            bet.id = index + 1;
        });

        setPreviewData(transformedData);
        setUploadStep('preview');
        setUploadError(null);
        return true;
    };

    // Apply uploaded data via API
    const applyUploadedData = async () => {
        setIsUploading(true);
        setUploadError(null);
        
        const result = await resultsTrackerService.bulkImportBets(previewData, uploadMode === 'replace');
        
        if (result.success) {
            const action = uploadMode === 'replace' ? 'imported' : 'added';
            
            if (uploadMode === 'add') {
                // Re-fetch all bets to get the full updated list
                const refreshResult = await resultsTrackerService.getAllData();
                if (refreshResult.success) {
                    setBets(refreshResult.data.bets || []);
                }
            } else {
                setBets(result.data.bets);
            }
            
            setSportFilter('all');
            setUploadStep('success');
            showToast(`${result.data.imported} bets ${action} successfully`, 'success');
            
            // Close modal after brief success message
            setTimeout(() => {
                resetUploadModal();
            }, 1500);
        } else {
            if (result.isAuthError) {
                handleAuthError(result.errorCode);
                resetUploadModal();
            } else {
                let errorMsg = result.message;
                if (result.details?.length > 0) {
                    if (typeof result.details[0] === 'object') {
                        // Bulk import errors with betIndex
                        const errors = result.details.slice(0, 5).map(d => 
                            `Row ${d.betIndex + 1}: ${d.errors.join(', ')}`
                        );
                        errorMsg = errors.join('\n');
                        if (result.details.length > 5) {
                            errorMsg += `\n... and ${result.details.length - 5} more errors`;
                        }
                    } else {
                        errorMsg = result.details.join('\n');
                    }
                }
                setUploadError(errorMsg);
            }
        }
        
        setIsUploading(false);
    };

    // Reset upload modal
    const resetUploadModal = () => {
        setShowUploadModal(false);
        setCsvData(null);
        setCsvHeaders([]);
        setColumnMapping({});
        setUploadError(null);
        setUploadStep('upload');
        setPreviewData([]);
        setUploadMode('replace');
        setSelectedSport('nba');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Available sports: hardcoded NBA/AFL + any additional sports found in bet data
    const availableSports = useMemo(() => {
        const hardcoded = ['nba', 'afl'];
        const hardcodedSet = new Set(hardcoded);
        const extraSports = [...new Set(bets.map(b => b.sport).filter(s => s && !hardcodedSet.has(s)))].sort();
        return [...hardcoded, ...extraSports];
    }, [bets]);

    // Filter bets by selected sport
    const filteredBets = useMemo(() => {
        if (sportFilter === 'all') return bets;
        return bets.filter(bet => bet.sport === sportFilter);
    }, [bets, sportFilter]);

    // Handle view change with transition animation
    const handleViewChange = (newView) => {
        if (newView === activeView) return;
        
        // Clear any pending timeout
        if (viewChangeTimeoutRef.current) {
            clearTimeout(viewChangeTimeoutRef.current);
        }

        // Start exit animation
        setIsViewTransitioning(true);
        
        // After exit animation, switch view and start enter animation
        viewChangeTimeoutRef.current = setTimeout(() => {
            setActiveView(newView);
            setDisplayedView(newView);
            
            // Small delay then remove transitioning state for enter animation
            setTimeout(() => {
                setIsViewTransitioning(false);
            }, 50);
        }, 200);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (viewChangeTimeoutRef.current) {
                clearTimeout(viewChangeTimeoutRef.current);
            }
        };
    }, []);

    // Calculate the date range from bet data for month picker
    const betDateRange = useMemo(() => {
        if (bets.length === 0) {
            const now = new Date();
            const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            return { min: currentMonth, max: currentMonth };
        }
        
        const dates = bets.map(b => new Date(b.date));
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        return {
            min: `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}`,
            max: `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}`
        };
    }, [bets]);

    // Get unit size for a specific date (used when advanced settings enabled)
    const getUnitSizeForDate = (dateStr) => {
        if (!advancedUnitSettings || monthlyUnits.length === 0) {
            return unitSize;
        }
        
        // Sort monthly units by start date descending
        const sortedUnits = [...monthlyUnits].sort((a, b) => 
            new Date(b.startDate) - new Date(a.startDate)
        );
        
        const betDate = new Date(dateStr);
        
        // Find the applicable unit size (first one where startDate <= betDate)
        for (const period of sortedUnits) {
            if (new Date(period.startDate) <= betDate) {
                return period.unitSize;
            }
        }
        
        // Fallback to default unit size
        return unitSize;
    };

    // Calculate comprehensive stats
    const stats = useMemo(() => {
        const totalBets = filteredBets.length;
        const wins = filteredBets.filter(b => b.result === 'win').length;
        const losses = filteredBets.filter(b => b.result === 'loss').length;
        const pending = filteredBets.filter(b => b.result === 'pending').length;
        const cashedOut = filteredBets.filter(b => b.result === 'cash out').length;
        const winRate = (wins + losses) > 0 ? ((wins / (wins + losses)) * 100).toFixed(2) : 0;
        
        // Exclude bonus bets from total staked (bonus bets use free money)
        const totalStaked = filteredBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake), 0);
        const totalPL = filteredBets.reduce((sum, b) => sum + b.pl, 0);
        // Calculate returned only for settled bets (exclude pending)
        // Pending bets have nothing returned yet
        const settledBets = filteredBets.filter(b => b.result !== 'pending');
        const totalReturned = settledBets.reduce((sum, b) => sum + (b.bonus_bet ? 0 : b.stake) + b.pl, 0);
        const roi = totalStaked > 0 ? ((totalPL / totalStaked) * 100).toFixed(2) : 0;
        
        const avgOdds = totalBets > 0 ? (filteredBets.reduce((sum, b) => sum + b.odds, 0) / totalBets).toFixed(2) : 0;
        const winningBets = filteredBets.filter(b => b.result === 'win');
        const avgWinOdds = winningBets.length > 0 
            ? (winningBets.reduce((sum, b) => sum + b.odds, 0) / winningBets.length).toFixed(2) 
            : 0;

        // Calculate streaks (ignore pending and cash out)
        let currentStreak = 0;
        let currentStreakType = null;
        let longestWinStreak = 0;
        let longestLossStreak = 0;
        let tempWinStreak = 0;
        let tempLossStreak = 0;

        const sortedBets = [...bets].sort((a, b) => new Date(a.date) - new Date(b.date));
        
        sortedBets.forEach(bet => {
            if (bet.result === 'win') {
                tempWinStreak++;
                tempLossStreak = 0;
                longestWinStreak = Math.max(longestWinStreak, tempWinStreak);
            } else if (bet.result === 'loss') {
                tempLossStreak++;
                tempWinStreak = 0;
                longestLossStreak = Math.max(longestLossStreak, tempLossStreak);
            }
            // Cash out and pending don't affect streaks
        });

        // Current streak (from most recent, skip pending and cash out)
        for (let i = sortedBets.length - 1; i >= 0; i--) {
            const bet = sortedBets[i];
            if (bet.result === 'pending' || bet.result === 'cash out') continue;
            
            if (currentStreakType === null) {
                currentStreakType = bet.result;
                currentStreak = 1;
            } else if (bet.result === currentStreakType) {
                currentStreak++;
            } else {
                break;
            }
        }

        return {
            totalBets,
            wins,
            losses,
            pending,
            cashedOut,
            winRate: parseFloat(winRate),
            totalStaked,
            totalReturned,
            totalPL,
            roi: parseFloat(roi),
            avgOdds: parseFloat(avgOdds),
            avgWinOdds: parseFloat(avgWinOdds),
            currentStreak,
            currentStreakType,
            longestWinStreak,
            longestLossStreak
        };
    }, [filteredBets]);

    // Calculate cumulative P/L for chart
    const cumulativePLData = useMemo(() => {
        const sortedBets = [...filteredBets].sort((a, b) => new Date(a.date) - new Date(b.date));
        let cumulative = 0;
        return sortedBets.map(bet => {
            cumulative += bet.pl;
            return {
                date: bet.date,
                pl: bet.pl,
                cumulative: cumulative,
                game: bet.game,
                bet: bet.bet
            };
        });
    }, [filteredBets]);

    // Add new bet handler
    const handleAddBet = async (newBet) => {
        setIsSaving(true);
        
        const betData = {
            ...newBet,
            pl: newBet.result === 'win' ? newBet.payout - newBet.stake : 
                newBet.result === 'loss' ? -newBet.stake : 
                newBet.result === 'cash out' ? newBet.payout - newBet.stake : 0
        };
        
        const result = await resultsTrackerService.addBet(betData);
        
        if (result.success) {
            setBets(prev => [...prev, result.data]);
            showToast('Bet added successfully', 'success');
        } else {
            if (result.isAuthError) {
                handleAuthError(result.errorCode);
            } else {
                const errorMsg = result.details?.length > 0 
                    ? result.details.join(', ') 
                    : result.message;
                showToast('Failed to add bet: ' + errorMsg, 'error');
            }
        }
        
        setIsSaving(false);
        return result;
    };

    // Bulk add bets handler
    const handleBulkAddBets = async (newBets) => {
        setIsSaving(true);
        
        // Bonus bet: win P/L = payout (stake was free), loss P/L = 0, cash out P/L = cash out amount
        // Normal bet: win P/L = payout - stake, loss P/L = -stake, cash out P/L = cash out - stake
        const betsData = newBets.map(bet => ({
            ...bet,
            pl: bet.bonus_bet
                ? (bet.result === 'win' ? bet.payout : 
                   bet.result === 'cash out' ? bet.payout : 0)
                : (bet.result === 'win' ? bet.payout - bet.stake : 
                   bet.result === 'loss' ? -bet.stake : 
                   bet.result === 'cash out' ? bet.payout - bet.stake : 0)
        }));
        
        const result = await resultsTrackerService.bulkImportBets(betsData, false);
        
        if (result.success) {
            // Check if API returned all bets or just new ones
            const returnedBets = result.data.bets || [];
            const importedCount = result.data.imported || returnedBets.length;
            
            // If returned bets count equals imported count, API returned only new bets - append them
            // If returned bets count is larger, API returned all bets - use them directly
            if (returnedBets.length === importedCount) {
                // Append new bets to existing ones
                setBets(prevBets => {
                    const existingIds = new Set(prevBets.map(b => b.id));
                    const newUniqueBets = returnedBets.filter(b => !existingIds.has(b.id));
                    return [...prevBets, ...newUniqueBets];
                });
            } else {
                // API returned all bets (existing + new)
                setBets(returnedBets);
            }
            showToast(`${importedCount} bet${importedCount !== 1 ? 's' : ''} added successfully`, 'success');
        } else {
            if (result.isAuthError) {
                handleAuthError(result.errorCode);
            } else {
                let errorMsg = result.message;
                if (result.details?.length > 0) {
                    if (typeof result.details[0] === 'object') {
                        // Bulk import errors with betIndex
                        const errors = result.details.slice(0, 3).map(d => 
                            `Row ${d.betIndex + 1}: ${d.errors.join(', ')}`
                        ).join('; ');
                        errorMsg = errors;
                    } else {
                        errorMsg = result.details.join(', ');
                    }
                }
                showToast('Failed to add bets: ' + errorMsg, 'error');
            }
        }
        
        setIsSaving(false);
        return result;
    };

    // Update bet handler
    const handleUpdateBet = async (updatedBet) => {
        setIsSaving(true);
        
        // Bonus bet: win P/L = payout (stake was free), loss P/L = 0, cash out P/L = cash out amount
        // Normal bet: win P/L = payout - stake, loss P/L = -stake, cash out P/L = cash out - stake
        const betData = {
            ...updatedBet,
            pl: updatedBet.bonus_bet
                ? (updatedBet.result === 'win' ? updatedBet.payout : 
                   updatedBet.result === 'cash out' ? updatedBet.payout : 0)
                : (updatedBet.result === 'win' ? updatedBet.payout - updatedBet.stake : 
                   updatedBet.result === 'loss' ? -updatedBet.stake : 
                   updatedBet.result === 'cash out' ? updatedBet.payout - updatedBet.stake : 0)
        };
        
        const result = await resultsTrackerService.updateBet(updatedBet.id, betData);
        
        if (result.success) {
            setBets(prev => prev.map(bet => 
                bet.id === updatedBet.id ? result.data : bet
            ));
            showToast('Bet updated successfully', 'success');
        } else {
            if (result.isAuthError) {
                handleAuthError(result.errorCode);
            } else if (result.status === 404) {
                // Bet was deleted, refresh the list
                showToast('This bet no longer exists', 'warning');
                const refreshResult = await resultsTrackerService.getAllData();
                if (refreshResult.success) {
                    setBets(refreshResult.data.bets || []);
                }
            } else {
                const errorMsg = result.details?.length > 0 
                    ? result.details.join(', ') 
                    : result.message;
                showToast('Failed to update bet: ' + errorMsg, 'error');
            }
        }
        
        setIsSaving(false);
        return result;
    };

    // Delete bet handler
    const handleDeleteBet = async (betId) => {
        setIsSaving(true);
        
        const result = await resultsTrackerService.deleteBet(betId);
        
        if (result.success) {
            // Refresh the full list to get reordered IDs
            const refreshResult = await resultsTrackerService.getAllData();
            if (refreshResult.success) {
                setBets(refreshResult.data.bets || []);
            } else {
                // Fallback: just remove locally
                setBets(prev => prev.filter(bet => bet.id !== betId));
            }
            showToast('Bet deleted successfully', 'success');
        } else {
            if (result.isAuthError) {
                handleAuthError(result.errorCode);
            } else if (result.status === 404) {
                // Bet already deleted, refresh
                showToast('Bet already deleted', 'info');
                const refreshResult = await resultsTrackerService.getAllData();
                if (refreshResult.success) {
                    setBets(refreshResult.data.bets || []);
                }
            } else {
                showToast('Failed to delete bet: ' + result.message, 'error');
            }
        }
        
        setIsSaving(false);
        return result;
    };

    // Reorder bets handler
    const handleReorderBets = async (date, betOrders) => {
        setIsSaving(true);
        
        const result = await resultsTrackerService.reorderBets(date, betOrders);
        
        if (result.success) {
            // Refresh full bets data from API to ensure consistency
            const refreshResult = await resultsTrackerService.getAllData();
            if (refreshResult.success) {
                setBets(refreshResult.data.bets || []);
            } else {
                // Fallback: Update local state manually if refresh fails
                setBets(prev => {
                    const updated = [...prev];
                    betOrders.forEach(({ id, sort_order }) => {
                        const betIndex = updated.findIndex(b => b.id === id);
                        if (betIndex !== -1) {
                            updated[betIndex] = { ...updated[betIndex], sort_order };
                        }
                    });
                    // Re-sort to reflect new order (date DESC, sort_order ASC)
                    return updated.sort((a, b) => {
                        const dateCompare = new Date(b.date) - new Date(a.date);
                        if (dateCompare !== 0) return dateCompare;
                        return (a.sort_order ?? a.id) - (b.sort_order ?? b.id);
                    });
                });
            }
            showToast('Bets reordered successfully', 'success');
        } else {
            if (result.isAuthError) {
                handleAuthError(result.errorCode);
            } else {
                showToast('Failed to reorder bets: ' + result.message, 'error');
            }
        }
        
        setIsSaving(false);
        return result;
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#020817] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-400">Loading your betting data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (loadError && bets.length === 0) {
        return (
            <div className="min-h-screen bg-[#020817] flex items-center justify-center p-4">
                <div className="bg-slate-900/50 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Failed to Load Data</h3>
                    <p className="text-slate-400 mb-6">{loadError}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#020817] p-4 md:p-6 lg:p-8 overflow-hidden">
            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Saving Indicator */}
            {isSaving && (
                <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 flex items-center gap-2 shadow-lg">
                    <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
                    <span className="text-sm text-slate-300">Saving...</span>
                </div>
            )}

            {/* CSS Animations */}
            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes fadeInScale {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .animate-fade-in-up {
                    animation: fadeInUp 0.5s ease-out forwards;
                }
                
                .animate-fade-in-scale {
                    animation: fadeInScale 0.4s ease-out forwards;
                }
                
                .animate-slide-in-right {
                    animation: slideInRight 0.4s ease-out forwards;
                }
                
                .animate-slide-in-left {
                    animation: slideInLeft 0.4s ease-out forwards;
                }
                
                .stagger-1 { animation-delay: 0.05s; }
                .stagger-2 { animation-delay: 0.1s; }
                .stagger-3 { animation-delay: 0.15s; }
                .stagger-4 { animation-delay: 0.2s; }
                .stagger-5 { animation-delay: 0.25s; }
                
                .view-enter {
                    opacity: 1;
                    transform: translateX(0);
                    transition: opacity 0.3s ease-out, transform 0.3s ease-out;
                }
                
                .view-exit {
                    opacity: 0;
                    transform: translateX(-20px);
                    transition: opacity 0.2s ease-in, transform 0.2s ease-in;
                }
            `}</style>

            {/* View Toggle */}
            <div 
                className={`mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 transition-all duration-500 ${
                    isPageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
            >
                {/* Top row on mobile: Sport Filter, Upload, Dollar/Units */}
                <div className="flex items-center justify-between gap-2 md:gap-4 flex-wrap">
                    {/* Sport Filter & Upload - Left side */}
                    <div className="flex items-center gap-2">
                        <select
                            value={sportFilter}
                            onChange={(e) => setSportFilter(e.target.value)}
                            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-xl text-sm text-white focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none pr-8"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 8px center',
                                backgroundSize: '16px'
                            }}
                        >
                            <option value="all">All Sports</option>
                            {availableSports.map(sport => (
                                <option key={sport} value={sport}>
                                    {sport.toUpperCase()}
                                </option>
                            ))}
                        </select>
                        
                        {/* Upload CSV Button */}
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-xl text-sm text-slate-300 hover:text-white hover:border-slate-600 transition-all flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            <span className="hidden sm:inline">Upload CSV</span>
                            <span className="sm:hidden">CSV</span>
                        </button>
                    </div>

                    {/* Dollar/Units Toggle */}
                    <div className="flex items-center gap-2">
                        <div className="flex bg-slate-800/50 rounded-xl p-1 border border-slate-700/50">
                            <button
                                onClick={() => updateDisplayMode('dollars')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    displayMode === 'dollars'
                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                $
                            </button>
                            <button
                                onClick={() => updateDisplayMode('units')}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    displayMode === 'units'
                                        ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                Units
                            </button>
                        </div>
                        {displayMode === 'units' && (
                            <div className="flex items-center gap-1 md:gap-2">
                                {!advancedUnitSettings && (
                                    <div className="hidden sm:flex items-center gap-1.5">
                                        <span className="text-xs text-slate-400">Unit:</span>
                                        <input
                                            type="number"
                                            value={unitSize}
                                            onChange={(e) => updateUnitSize(parseFloat(e.target.value) || 1)}
                                            className="w-14 md:w-16 px-2 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-white text-center focus:outline-none focus:border-emerald-500"
                                            min="1"
                                            step="1"
                                        />
                                    </div>
                                )}
                                {advancedUnitSettings && (
                                    <span className="hidden sm:inline text-xs text-slate-400">
                                        {monthlyUnits.length} month{monthlyUnits.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                                <button
                                    onClick={() => setShowUnitSettingsModal(true)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                                    title="Unit Settings"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Analytics/Bet Log Toggle - Full width on mobile */}
                <div className="flex bg-slate-800/50 rounded-xl p-1 border border-slate-700/50 w-full md:w-auto">
                    <button
                        onClick={() => handleViewChange('overview')}
                        className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            activeView === 'overview'
                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                : 'text-slate-400 hover:text-white'
                        }`}
                    >
                        <span className="flex items-center justify-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            Analytics
                        </span>
                    </button>
                    <button
                        onClick={() => handleViewChange('tracker')}
                        className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            activeView === 'tracker'
                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                : 'text-slate-400 hover:text-white'
                        }`}
                    >
                        <span className="flex items-center justify-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Bet Log
                        </span>
                    </button>
                </div>
            </div>

            {/* Content with view transitions */}
            <div className={`transition-all duration-300 ease-out ${
                isViewTransitioning ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
            }`}>
                {activeView === 'overview' ? (
                    <>
                        {/* Stats Overview Cards */}
                        <div className={`transition-all duration-500 ${
                            isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`} style={{ transitionDelay: '100ms' }}>
                            <StatsOverview stats={stats} displayMode={displayMode} unitSize={unitSize} bets={filteredBets} getUnitSizeForDate={getUnitSizeForDate} advancedUnitSettings={advancedUnitSettings} />
                        </div>

                        {/* Row 1: P/L Performance Chart (Full Width) */}
                        <div className={`mb-6 transition-all duration-500 ${
                            isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`} style={{ transitionDelay: '150ms' }}>
                            <PerformanceChart data={cumulativePLData} bets={filteredBets} />
                        </div>

                        {/* Row 2: Bookmaker Breakdown & Monthly Calendar */}
                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 transition-all duration-500 ${
                            isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                        }`} style={{ transitionDelay: '200ms' }}>
                            <BookmakerBreakdown bets={filteredBets} displayMode={displayMode} unitSize={unitSize} getUnitSizeForDate={getUnitSizeForDate} advancedUnitSettings={advancedUnitSettings} />
                            <MonthlyCalendar bets={filteredBets} displayMode={displayMode} unitSize={unitSize} getUnitSizeForDate={getUnitSizeForDate} advancedUnitSettings={advancedUnitSettings} />
                        </div>

                        {/* Row 3: Sport Breakdown (only when viewing all sports) */}
                        {sportFilter === 'all' && (
                            <div className={`mb-6 transition-all duration-500 ${
                                isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                            }`} style={{ transitionDelay: '250ms' }}>
                                <SportBreakdown bets={filteredBets} displayMode={displayMode} unitSize={unitSize} getUnitSizeForDate={getUnitSizeForDate} advancedUnitSettings={advancedUnitSettings} />
                            </div>
                        )}

                        {/* Row 4: AFL Round Breakdown (only when viewing AFL) */}
                        {sportFilter === 'afl' && (
                            <div className={`mb-6 transition-all duration-500 ${
                                isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                            }`} style={{ transitionDelay: '300ms' }}>
                                <RoundBreakdown bets={filteredBets} displayMode={displayMode} unitSize={unitSize} getUnitSizeForDate={getUnitSizeForDate} advancedUnitSettings={advancedUnitSettings} />
                            </div>
                        )}
                    </>
                ) : (
                    /* Bet Tracking Spreadsheet */
                    <div className={`transition-all duration-500 ${
                        isPageLoaded && !isViewTransitioning ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`} style={{ transitionDelay: '100ms' }}>
                        <BetTracker 
                            bets={filteredBets} 
                            onAddBet={handleAddBet}
                            onBulkAddBets={handleBulkAddBets}
                            onUpdateBet={handleUpdateBet}
                            onDeleteBet={handleDeleteBet}
                            onReorderBets={handleReorderBets}
                            displayMode={displayMode}
                            unitSize={unitSize}
                            getUnitSizeForDate={getUnitSizeForDate}
                            advancedUnitSettings={advancedUnitSettings}
                            isSaving={isSaving}
                            sportOptions={sportOptions}
                        />
                    </div>
                )}
            </div>

            {/* Unit Settings Modal */}
            {showUnitSettingsModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-white">Unit Size Settings</h3>
                            <button
                                onClick={() => setShowUnitSettingsModal(false)}
                                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Simple vs Advanced Toggle */}
                        <div className="mb-6">
                            <div className="flex bg-slate-800/50 rounded-xl p-1 border border-slate-700/50">
                                <button
                                    onClick={() => updateAdvancedSettings(false)}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        !advancedUnitSettings
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                >
                                    Simple
                                </button>
                                <button
                                    onClick={() => updateAdvancedSettings(true)}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        advancedUnitSettings
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20'
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                >
                                    Advanced
                                </button>
                            </div>
                        </div>

                        {!advancedUnitSettings ? (
                            /* Simple Mode */
                            <div className="space-y-4">
                                <p className="text-sm text-slate-400">
                                    Set a single unit size that applies to all your bets.
                                </p>
                                <div className="flex items-center gap-3">
                                    <label className="text-sm text-slate-300">Unit Size:</label>
                                    <div className="flex items-center">
                                        <span className="text-slate-400 mr-1">$</span>
                                        <input
                                            type="number"
                                            value={unitSize}
                                            onChange={(e) => updateUnitSize(parseFloat(e.target.value) || 1)}
                                            className="w-24 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                            min="1"
                                            step="1"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Advanced Mode */
                            <div className="space-y-4">
                                <p className="text-sm text-slate-400">
                                    Set different unit sizes for different months. Each unit size applies from the start of the selected month.
                                </p>
                                
                                {/* Default Unit Size */}
                                <div className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                                    <label className="text-sm text-slate-300 flex-1">Default (before first month):</label>
                                    <div className="flex items-center">
                                        <span className="text-slate-400 mr-1">$</span>
                                        <input
                                            type="number"
                                            value={unitSize}
                                            onChange={(e) => updateUnitSize(parseFloat(e.target.value) || 1)}
                                            className="w-20 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                                            min="1"
                                            step="1"
                                        />
                                    </div>
                                </div>

                                {/* Period List */}
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {monthlyUnits.map((period, index) => (
                                        <div key={index} className="flex items-center gap-2 p-3 bg-slate-800/30 rounded-lg border border-slate-700/30">
                                            <input
                                                type="month"
                                                value={period.startDate.substring(0, 7)}
                                                min={betDateRange.min}
                                                max={betDateRange.max}
                                                onChange={(e) => {
                                                    const updated = [...monthlyUnits];
                                                    updated[index].startDate = e.target.value + '-01';
                                                    updateMonthlyUnits(updated);
                                                }}
                                                className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500 [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:brightness-0 [&::-webkit-calendar-picker-indicator]:saturate-100 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:sepia [&::-webkit-calendar-picker-indicator]:saturate-[10000%] [&::-webkit-calendar-picker-indicator]:hue-rotate-[115deg] [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                            />
                                            <div className="flex items-center">
                                                <span className="text-slate-400 mr-1 text-sm">$</span>
                                                <input
                                                    type="number"
                                                    value={period.unitSize}
                                                    onChange={(e) => {
                                                        const updated = [...monthlyUnits];
                                                        updated[index].unitSize = parseFloat(e.target.value) || 1;
                                                        updateMonthlyUnits(updated);
                                                    }}
                                                    className="w-20 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                                                    min="1"
                                                    step="1"
                                                />
                                            </div>
                                            <button
                                                onClick={() => {
                                                    updateMonthlyUnits(monthlyUnits.filter((_, i) => i !== index));
                                                }}
                                                className="p-1.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {/* Add Period Button */}
                                <button
                                    onClick={() => {
                                        // Default to the earliest month in bet data
                                        const monthStart = betDateRange.min + '-01';
                                        updateMonthlyUnits([...monthlyUnits, { startDate: monthStart, unitSize: unitSize }]);
                                    }}
                                    className="w-full py-2 px-4 rounded-lg border border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 transition-all text-sm flex items-center justify-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add Month
                                </button>
                            </div>
                        )}

                        {/* Close Button */}
                        <div className="mt-6 pt-4 border-t border-slate-700">
                            <button
                                onClick={() => setShowUnitSettingsModal(false)}
                                className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSV Upload Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-white">Upload Bet Data</h3>
                            <button
                                onClick={resetUploadModal}
                                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Progress Steps */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            {['upload', 'mapping', 'preview'].map((step, index) => (
                                <div key={step} className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                                        uploadStep === step 
                                            ? 'bg-emerald-500 text-white' 
                                            : ['mapping', 'preview', 'success'].indexOf(uploadStep) > ['upload', 'mapping', 'preview'].indexOf(step)
                                                ? 'bg-emerald-500/20 text-emerald-400'
                                                : 'bg-slate-700 text-slate-400'
                                    }`}>
                                        {index + 1}
                                    </div>
                                    {index < 2 && (
                                        <div className={`w-12 h-0.5 mx-1 ${
                                            ['mapping', 'preview', 'success'].indexOf(uploadStep) > index
                                                ? 'bg-emerald-500/50'
                                                : 'bg-slate-700'
                                        }`} />
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Error Display */}
                        {uploadError && (
                            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <pre className="text-sm text-red-400 whitespace-pre-wrap">{uploadError}</pre>
                                </div>
                            </div>
                        )}

                        {/* Step 1: Upload */}
                        {uploadStep === 'upload' && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-400">
                                    Upload a CSV file containing your bet data. The file should have headers that can be mapped to our required fields.
                                </p>

                                {/* Import Mode Dropdown */}
                                <div className="bg-slate-800/30 rounded-lg p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-shrink-0">
                                            <span className="text-sm text-slate-300 font-medium">Import Mode</span>
                                        </div>
                                        <select
                                            value={uploadMode}
                                            onChange={(e) => setUploadMode(e.target.value)}
                                            className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                                        >
                                            <option value="replace">Replace all existing bets</option>
                                            <option value="add">Add to existing bets</option>
                                        </select>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">
                                        {uploadMode === 'replace' 
                                            ? 'All current bet data will be replaced with the uploaded data.' 
                                            : 'Uploaded bets will be added alongside your existing bet data.'}
                                    </p>
                                </div>
                                
                                <div 
                                    className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-slate-500 transition-all cursor-pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                    <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <p className="text-slate-300 font-medium mb-1">Click to upload CSV</p>
                                    <p className="text-sm text-slate-500">or drag and drop</p>
                                </div>

                                <div className="bg-slate-800/30 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-white mb-2">Required Fields</h4>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        {allFields.map(field => (
                                            <div key={field.key} className="flex items-center gap-2">
                                                <span className="text-emerald-400">{field.label}</span>
                                                <span className="text-slate-500">- {field.description}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Column Mapping */}
                        {uploadStep === 'mapping' && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-400">
                                    Select the sport and map your CSV columns to the required fields.
                                </p>
                                
                                <div className="bg-slate-800/30 rounded-lg p-4 text-sm text-slate-400">
                                    Found <span className="text-white font-medium">{csvData?.length || 0}</span> rows with <span className="text-white font-medium">{csvHeaders.length}</span> columns
                                </div>

                                {/* Sport Selection */}
                                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 flex-shrink-0">
                                            <span className="text-sm text-emerald-400 font-medium">Sport</span>
                                            <span className="text-red-400 ml-0.5">*</span>
                                        </div>
                                        <svg className="w-4 h-4 text-emerald-500/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                        {selectedSport === '__other__' ? (
                                            <div className="flex-1 flex gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="Enter sport name"
                                                    defaultValue={customUploadSport}
                                                    onChange={(e) => { customUploadSportRef.current = e.target.value; }}
                                                    onBlur={(e) => setCustomUploadSport(e.target.value)}
                                                    autoFocus
                                                    className="flex-1 px-3 py-2 bg-slate-800 border border-emerald-500/50 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                                                />
                                                <button
                                                    onClick={() => { setSelectedSport('nba'); setCustomUploadSport(''); customUploadSportRef.current = ''; }}
                                                    className="px-2 text-slate-400 hover:text-white transition-colors"
                                                    title="Back to dropdown"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <select
                                                value={selectedSport}
                                                onChange={(e) => {
                                                    if (e.target.value === '__other__') {
                                                        setSelectedSport('__other__');
                                                        setCustomUploadSport('');
                                                    } else {
                                                        setSelectedSport(e.target.value);
                                                    }
                                                }}
                                                className="flex-1 px-3 py-2 bg-slate-800 border border-emerald-500/50 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                                            >
                                                {sportOptions.map(sport => (
                                                    <option key={sport.value} value={sport.value}>{sport.label}</option>
                                                ))}
                                                <option value="__other__">Other...</option>
                                            </select>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2 ml-[116px]">All bets will be assigned to this sport</p>
                                </div>

                                {/* Column Mapping */}
                                <div className="space-y-3 max-h-52 overflow-y-auto">
                                    {requiredFields.map(field => (
                                        <div key={field.key} className="flex items-center gap-3">
                                            <div className="w-24 flex-shrink-0">
                                                <span className="text-sm text-slate-300">{field.label}</span>
                                                <span className="text-red-400 ml-0.5">*</span>
                                            </div>
                                            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                            </svg>
                                            <select
                                                value={columnMapping[field.key] || ''}
                                                onChange={(e) => setColumnMapping({
                                                    ...columnMapping,
                                                    [field.key]: e.target.value
                                                })}
                                                className={`flex-1 px-3 py-2 bg-slate-800 border rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500 ${
                                                    columnMapping[field.key] ? 'border-emerald-500/50' : 'border-slate-700'
                                                }`}
                                            >
                                                <option value="">Select column...</option>
                                                {csvHeaders.map(header => (
                                                    <option key={header} value={header}>{header}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => setUploadStep('upload')}
                                        className="flex-1 py-2.5 px-4 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 transition-all"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={validateAndTransform}
                                        className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20"
                                    >
                                        Validate & Preview
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Preview */}
                        {uploadStep === 'preview' && (
                            <div className="space-y-4">
                                <p className="text-sm text-slate-400">
                                    Review your data before importing. All <span className="text-emerald-400 font-medium">{previewData.length}</span> bets are valid.
                                </p>
                                
                                <div className="bg-slate-800/30 rounded-lg overflow-hidden">
                                    <div className="overflow-x-auto max-h-64">
                                        <table className="w-full text-xs">
                                            <thead className="bg-slate-800 sticky top-0">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-slate-400 font-medium">ID</th>
                                                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Date</th>
                                                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Sport</th>
                                                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Game</th>
                                                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Bet</th>
                                                    <th className="px-3 py-2 text-left text-slate-400 font-medium">Result</th>
                                                    <th className="px-3 py-2 text-right text-slate-400 font-medium">P/L</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-700/50">
                                                {previewData.slice(0, 10).map((bet) => (
                                                    <tr key={bet.id} className="hover:bg-slate-800/30">
                                                        <td className="px-3 py-2 text-slate-500">{bet.id}</td>
                                                        <td className="px-3 py-2 text-slate-300">{bet.date}</td>
                                                        <td className="px-3 py-2 text-slate-300 uppercase">{bet.sport}</td>
                                                        <td className="px-3 py-2 text-slate-300 max-w-[100px] truncate">{bet.game}</td>
                                                        <td className="px-3 py-2 text-slate-300 max-w-[150px] truncate">{bet.bet}</td>
                                                        <td className="px-3 py-2">
                                                            <span className={`px-2 py-0.5 rounded text-xs ${
                                                                bet.result === 'win' ? 'bg-emerald-500/20 text-emerald-400' :
                                                                bet.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                                                                bet.result === 'cash out' ? 'bg-blue-500/20 text-blue-400' :
                                                                'bg-yellow-500/20 text-yellow-400'
                                                            }`}>
                                                                {bet.result}
                                                            </span>
                                                        </td>
                                                        <td className={`px-3 py-2 text-right font-medium ${bet.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                            {bet.pl >= 0 ? '+' : ''}{bet.pl.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {previewData.length > 10 && (
                                        <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-700/50">
                                            ... and {previewData.length - 10} more bets
                                        </div>
                                    )}
                                </div>

                                <div className="bg-slate-800/30 rounded-lg p-3 flex items-center justify-between text-sm">
                                    <span className="text-slate-400">Total P/L:</span>
                                    <span className={`font-bold ${previewData.reduce((s, b) => s + b.pl, 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        ${previewData.reduce((s, b) => s + b.pl, 0).toFixed(2)}
                                    </span>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={() => setUploadStep('mapping')}
                                        className="flex-1 py-2.5 px-4 rounded-lg border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 transition-all"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={applyUploadedData}
                                        disabled={isUploading}
                                        className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isUploading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                Importing...
                                            </>
                                        ) : (
                                            `Import ${previewData.length} Bets`
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Success */}
                        {uploadStep === 'success' && (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h4 className="text-lg font-semibold text-white mb-2">Import Successful!</h4>
                                <p className="text-slate-400">{previewData.length} bets have been imported</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ResultsTracker;
